import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { PanelHeaderSummary } from 'components/StudyDashboard/MultiplexPanels/PanelHeaderSummary';
import { PanelSlideSummary } from 'components/StudyDashboard/MultiplexPanels/PanelSlideSummary';
import Slider from 'components/atoms/Slider';
import { MultiplexPanelView } from 'interfaces/multiplexPanels';
import { every, filter, find, includes, keys, map, reduce, size, sumBy } from 'lodash';
import React, { useId } from 'react';
import { SpotDetectionMesh } from './SpotDetectionMesh';
import { buildPeakDetectionSpacingDistanceGroups, getPeakDetectionUmppFromSlideMetadata } from './helpers';
import { MultiplexMogPeakDetectionChannelProfile, PanelPeakDetectionConfiguration } from './types';

interface Props {
  panel: MultiplexPanelView;
  configuration: PanelPeakDetectionConfiguration;
  selectedSlideIds: string[];
  onChange: (value: PanelPeakDetectionConfiguration) => void;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  disabled?: boolean;
  included?: boolean;
  onIncludeChange?: (included: boolean) => void;
  selectionDisabled?: boolean;
  labId?: string;
  customResolutionUmpp?: number | null;
}

const ProfileFields = ({
  profile,
  onChange,
  label,
  renderIllustration,
  disabled,
}: {
  profile: MultiplexMogPeakDetectionChannelProfile;
  onChange: (value: MultiplexMogPeakDetectionChannelProfile) => void;
  label: string;
  renderIllustration?: (controls: React.ReactNode) => React.ReactNode;
  disabled?: boolean;
}) => {
  const fieldId = useId();
  const fields: Array<{
    key: string;
    label: string;
    optional: boolean;
    min: number;
    max?: number;
    step: number;
  }> = [
    {
      key: 'clipMinFraction',
      label: 'Lower clipping fraction',
      optional: false,
      min: 0,
      max: 1,
      step: 0.001,
    },
    { key: 'minDistancePx', label: 'Minimum spot spacing (px)', optional: false, min: 1, max: undefined, step: 1 },
    {
      key: 'minRadiusUm',
      label: 'Minimum spot radius (micrometres)',
      optional: true,
      min: 0,
      max: undefined,
      step: 0.01,
    },
    {
      key: 'maxRadiusUm',
      label: 'Maximum spot radius (micrometres)',
      optional: true,
      min: 0,
      max: undefined,
      step: 0.01,
    },
    { key: 'minContrast', label: 'Minimum contrast', optional: false, min: 0, max: 1, step: 0.001 },
  ];
  const renderField = (field: (typeof fields)[number]) => {
    const value = (profile as unknown as Record<string, number | null>)[field.key];
    const invalidRadiusRange =
      profile.minRadiusUm != null && profile.maxRadiusUm != null && profile.minRadiusUm > profile.maxRadiusUm;
    const invalid =
      value != null &&
      (!Number.isFinite(value) ||
        value < field.min ||
        (field.max != null && value > field.max) ||
        (field.key === 'clipMinFraction' && value >= 1) ||
        (field.key === 'minDistancePx' && !Number.isInteger(value)) ||
        (field.optional && invalidRadiusRange));
    const sliderMaximum =
      field.max != null
        ? field.key === 'clipMinFraction'
          ? 0.999
          : field.max
        : Math.max(field.key === 'minDistancePx' ? 12 : 3, Number.isFinite(value) ? Math.ceil(value!) : 0);
    const marks = Array.from(
      new Set([
        field.min,
        ...[0.25, 0.5, 0.75, 1].map((fraction) =>
          fraction === 1
            ? sliderMaximum
            : field.key === 'minDistancePx'
            ? Math.round(sliderMaximum * fraction)
            : Number((sliderMaximum * fraction).toFixed(2))
        ),
      ])
    )
      .filter((mark) => mark >= field.min && mark <= sliderMaximum)
      .map((mark) => ({ value: mark, label: String(mark) }));
    const title =
      field.key === 'clipMinFraction'
        ? 'Lower clipping'
        : field.key === 'minContrast'
        ? 'Minimum contrast'
        : field.key === 'minDistancePx'
        ? 'Minimum spacing (px)'
        : field.key === 'minRadiusUm'
        ? 'Minimum radius (\u00b5m)'
        : 'Maximum radius (\u00b5m)';
    const helper =
      invalid && field.key === 'minContrast'
        ? 'Enter a minimum contrast between 0 and 1 (inclusive).'
        : invalidRadiusRange && field.optional
        ? 'Minimum radius must not exceed maximum.'
        : field.optional
        ? field.key === 'minRadiusUm'
          ? 'No minimum when empty.'
          : 'No maximum when empty.'
        : field.key === 'clipMinFraction'
        ? 'Fraction of upper clipping. Default: 0.03 (3%).'
        : field.key === 'minContrast'
        ? 'Minimum peak above local background after clipping and rescaling. Default: 0.1 (10%). Lower values include fainter spots and may include more noise.'
        : 'Minimum: 1 working pixel. Default: 1 px.';
    return (
      <Stack key={field.key} spacing={0.5} data-spot-field={field.key}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Typography component="label" htmlFor={`${fieldId}-${field.key}`} variant="body2" sx={{ fontWeight: 500 }}>
            {title}
          </Typography>
          <TextField
            size="small"
            id={`${fieldId}-${field.key}`}
            type="number"
            placeholder={field.optional ? 'None' : undefined}
            sx={{ width: '84px', flexShrink: 0, '& input': { px: 1, py: 0.75, fontVariantNumeric: 'tabular-nums' } }}
            value={value == null || !Number.isFinite(value) ? '' : value}
            inputProps={{
              min: field.min,
              max: field.max,
              step: field.step,
              'aria-label': `${label}: ${field.label}`,
              'aria-describedby': `${fieldId}-${field.key}-help`,
            }}
            error={invalid}
            disabled={disabled}
            onChange={(event) =>
              onChange({
                ...profile,
                [field.key]: event.target.value === '' ? (field.optional ? null : NaN) : Number(event.target.value),
              })
            }
          />
        </Stack>
        <Box sx={{ px: 1, pt: 0.5 }}>
          <Slider
            aria-label={`${label}: ${field.label}`}
            slotProps={{ input: { 'aria-describedby': `${fieldId}-${field.key}-help` } }}
            min={field.min}
            max={sliderMaximum}
            step={field.step}
            value={value != null && Number.isFinite(value) ? value : field.min}
            disabled={disabled}
            alwaysShowValue
            marks={marks}
            track={field.optional && value == null ? false : 'normal'}
            sx={{ mb: 1, '& .MuiSlider-markLabel': { fontSize: 10, color: 'text.secondary' } }}
            onValueChange={(nextValue) => onChange({ ...profile, [field.key]: Number(nextValue) })}
            color="secondary"
          />
        </Box>
        <Typography
          id={`${fieldId}-${field.key}-help`}
          variant="caption"
          color={invalid ? 'error' : 'text.secondary'}
          sx={{ lineHeight: 1.35 }}
        >
          {helper}
        </Typography>
      </Stack>
    );
  };
  return renderIllustration ? (
    renderIllustration(
      <Stack data-testid="spot-default-settings" spacing={2}>
        {map(fields, renderField)}
      </Stack>
    )
  ) : (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 2 }}>
      {map(fields, renderField)}
    </Box>
  );
};

export const PanelPeakDetectionEditor = ({
  panel,
  configuration,
  selectedSlideIds,
  onChange,
  expanded,
  onExpandedChange,
  disabled,
  included = true,
  onIncludeChange,
  selectionDisabled,
  labId,
  customResolutionUmpp,
}: Props) => {
  const scopedSlides = filter(panel.slides, (slide) => includes(selectedSlideIds, slide.slideId));
  const umppBySlideId = reduce(
    scopedSlides,
    (result, slide) => {
      const umpp = getPeakDetectionUmppFromSlideMetadata(slide);
      if (umpp != null) result[slide.slideId] = umpp;
      return result;
    },
    {} as Record<string, number>
  );
  const invalidCustomResolution =
    customResolutionUmpp != null && (!Number.isFinite(customResolutionUmpp) || customResolutionUmpp <= 0);
  const distanceGroups = invalidCustomResolution
    ? []
    : buildPeakDetectionSpacingDistanceGroups({
        minDistancePx: configuration.defaultProfile.minDistancePx,
        slideIds: map(scopedSlides, 'slideId'),
        umppBySlideId,
        customResolutionUmpp,
      });
  const unknownResolutionCount = Math.max(0, size(scopedSlides) - sumBy(distanceGroups, 'slideCount'));
  const options = filter(
    panel.channels,
    (channel) => Boolean(channel.saved) && channel.saved?.channelModality !== 'dna'
  );
  const isRna = (analysisName: string) => {
    const channel = find(options, (entry) => entry.saved?.analysisName === analysisName);
    return Boolean(
      channel &&
        size(scopedSlides) &&
        every(scopedSlides, (slide) => {
          const index = find(keys(slide.channelMapping), (key) => slide.channelMapping[key] === channel.channelIndex);
          return index != null && includes(slide.rnaChannelIds, index);
        })
    );
  };
  const markerLabel = (analysisName: string) => {
    const channel = find(options, (entry) => entry.saved?.analysisName === analysisName);
    return channel?.saved?.displayName || channel?.saved?.stainTypeId || analysisName;
  };
  const panelName = panel.name || 'Multiplex panel';
  const selector = (value: string[], change: (selected: string[]) => void, label: string, available: string[]) => (
    <Autocomplete
      multiple
      disableCloseOnSelect
      options={available}
      value={value}
      getOptionLabel={markerLabel}
      onChange={(_, selected) => change(selected)}
      disabled={disabled}
      renderTags={(selected, getTagProps) =>
        map(selected, (marker, index) => (
          <Chip
            {...getTagProps({ index })}
            label={`${markerLabel(marker)} (${isRna(marker) ? 'RNA' : 'mIF'})`}
            size="small"
          />
        ))
      }
      renderInput={(params) => <TextField {...params} label={label} />}
    />
  );
  return (
    <Accordion
      expanded={expanded}
      onChange={(_, next) => onExpandedChange(next)}
      disableGutters
      TransitionProps={{ timeout: 200 }}
      sx={{ bgcolor: (theme) => (theme.palette.mode === 'light' ? 'common.white' : 'background.paper') }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: 'action.hover' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-label={panelName} sx={{ flex: 1, minWidth: 0 }}>
          <PanelHeaderSummary
            title={panelName}
            markerNames={map(options, (channel) => markerLabel(channel.saved!.analysisName).trim())}
          >
            <Chip size="small" label={`${size(scopedSlides)} ${size(scopedSlides) === 1 ? 'slide' : 'slides'}`} />
          </PanelHeaderSummary>
        </AccordionSummary>
        {onIncludeChange ? (
          <FormControlLabel
            sx={{ mr: 2, ml: 1 }}
            label="Include"
            control={
              <Switch
                size="small"
                color="primary"
                checked={included}
                disabled={selectionDisabled}
                inputProps={{ role: 'switch', 'aria-label': `Include ${panelName} in run` }}
                onChange={(_, checked) => onIncludeChange(checked)}
              />
            }
          />
        ) : null}
      </Box>
      <AccordionDetails>
        <Stack spacing={2} component="fieldset" disabled={disabled} sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}>
          {selector(
            configuration.channelNames,
            (channelNames) => onChange({ ...configuration, channelNames }),
            `${panelName}: Markers`,
            map(options, (channel) => channel.saved!.analysisName)
          )}
          <PanelSlideSummary panel={{ ...panel, slides: scopedSlides }} labId={labId} />
          {size(filter(configuration.channelNames, (marker) => !isRna(marker))) > 0 ? (
            <Alert severity="warning">
              Non-RNA markers are selected. Spot detection will run on their assigned signals; their modality will not
              change.
            </Alert>
          ) : null}
          <Typography variant="subtitle2">Panel defaults</Typography>
          <ProfileFields
            profile={configuration.defaultProfile}
            disabled={disabled}
            label={`${panelName} defaults`}
            onChange={(defaultProfile) => onChange({ ...configuration, defaultProfile })}
            renderIllustration={(controls) => (
              <SpotDetectionMesh
                controls={controls}
                profile={configuration.defaultProfile}
                panelName={panelName}
                distanceGroups={distanceGroups}
                unknownResolutionCount={unknownResolutionCount}
                bitDepths={scopedSlides.map((slide) => slide.bitDepth)}
              />
            )}
          />
          {map(configuration.overrides, (override, overrideIndex) => (
            <Stack spacing={1} key={overrideIndex}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle2">Override {overrideIndex + 1}</Typography>
                <Tooltip title="Remove override">
                  <IconButton
                    color="inherit"
                    aria-label={`Remove override ${overrideIndex + 1} from ${panelName}`}
                    onClick={() =>
                      onChange({
                        ...configuration,
                        overrides: filter(configuration.overrides, (_, index) => index !== overrideIndex),
                      })
                    }
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </Tooltip>
              </Box>
              {selector(
                override.channelNames,
                (channelNames) =>
                  onChange({
                    ...configuration,
                    overrides: map(configuration.overrides, (entry, index) =>
                      index === overrideIndex ? { ...entry, channelNames } : entry
                    ),
                  }),
                `${panelName}: Override ${overrideIndex + 1} markers`,
                configuration.channelNames
              )}
              <ProfileFields
                profile={override.profile}
                disabled={disabled}
                label={`${panelName} override ${overrideIndex + 1}`}
                onChange={(profile) =>
                  onChange({
                    ...configuration,
                    overrides: map(configuration.overrides, (entry, index) =>
                      index === overrideIndex ? { ...entry, profile } : entry
                    ),
                  })
                }
              />
              <Typography variant="caption" color="text.secondary">
                Other selected markers keep the panel defaults.
              </Typography>
            </Stack>
          ))}
          <Box>
            <Button
              color="secondary"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() =>
                onChange({
                  ...configuration,
                  overrides: [
                    ...configuration.overrides,
                    { channelNames: [], profile: { ...configuration.defaultProfile } },
                  ],
                })
              }
            >
              Add override
            </Button>
          </Box>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};
